import React, { useState, useEffect, useRef } from 'react';
import { SearchResult, searchApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Search, Xmark, BoxIso } from 'iconoir-react';

export const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fermer les résultats si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        performSearch();
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, includeArchived]);

  const performSearch = async () => {
    setIsSearching(true);
    setError(null);
    try {
      const response = await searchApi.searchCards(query.trim(), includeArchived);
      setResults(response.data.results);
      setShowResults(true);
    } catch (err: any) {
      setError('Erreur lors de la recherche');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setShowResults(false);
    setQuery('');
    
    // Si la carte est archivée, rediriger vers la page d'archivage
    if (result.archived) {
      window.location.href = `/boards/${result.board_id}/archived?card=${result.id}`;
    } else {
      // Sinon, naviguer vers le tableau normal
      window.location.href = `/?board=${result.board_id}&card=${result.id}`;
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? <mark key={index}>{part}</mark> : part
    );
  };

  return (
    <div className="search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <Search className="search-icon" width={18} height={18} strokeWidth={2} />
        <input
          type="text"
          className="search-input"
          placeholder="Rechercher une carte..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setShowResults(true)}
        />
        {query && (
          <button className="search-clear" onClick={handleClear}>
            <Xmark width={16} height={16} strokeWidth={2} />
          </button>
        )}
        <button 
          className={`search-toggle-archived ${includeArchived ? 'active' : ''}`}
          onClick={() => setIncludeArchived(!includeArchived)}
          title={includeArchived ? 'Exclure les archives' : 'Inclure les archives'}
        >
          <BoxIso width={16} height={16} strokeWidth={2} />
        </button>
      </div>

      {showResults && (
        <div className="search-results">
          {isSearching ? (
            <div className="search-loading">Recherche en cours...</div>
          ) : error ? (
            <div className="search-error">{error}</div>
          ) : results.length === 0 ? (
            <div className="search-empty">Aucun résultat trouvé</div>
          ) : (
            <>
              <div className="search-results-header">
                {results.length} résultat{results.length > 1 ? 's' : ''}
              </div>
              <div className="search-results-list">
                {results.map((result) => (
                  <div
                    key={result.id}
                    className="search-result-item"
                    onClick={() => handleResultClick(result)}
                  >
                    <div className="search-result-header">
                      <div 
                        className="search-result-board-badge"
                        style={{ backgroundColor: result.board_color || '#00bf7e' }}
                      >
                        {result.board_title}
                      </div>
                      <div className="search-result-column">
                        {result.column_title}
                      </div>
                      {result.archived && (
                        <div className="search-result-archived-badge">
                          <BoxIso width={14} height={14} strokeWidth={2} />
                          Archivée
                        </div>
                      )}
                    </div>
                    <div className="search-result-title">
                      {highlightMatch(result.title, query)}
                    </div>
                    {result.description && (
                      <div className="search-result-description">
                        {highlightMatch(
                          result.description.substring(0, 100) + 
                          (result.description.length > 100 ? '...' : ''),
                          query
                        )}
                      </div>
                    )}
                    <div className="search-result-meta">
                      Trouvé dans : {result.match_in}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
